interface VehicleColorSetupOptions {
  /** The angle of the gradient in degrees between 0 and 360. Default is 90. */
  gradientAngle?: number;
  /** The smoothness of the gradient between 0 and 100. Default is 20. */
  gradientSmoothness?: number;
}

/**
 * VehicleColor class for setting up a vehicle color selector.
 * @param selector - CSS selector for the container element.
 * @param imageWidth - Width of the vehicle image.
 * @param imageHeight - Height of the
 * @param options - Optional setup options.
 */
export class VehicleColor {
  private root: HTMLElement; // The root element containing the vehicle color elements.
  private imageWidth: number; // Width of the vehicle image.
  private imageHeight: number; // Height of the vehicle image
  private options: VehicleColorSetupOptions | null; // Configuration options.

  constructor(
    selector: string, // CSS selector for the container element.
    imageWidth: number, // Width of the vehicle image.
    imageHeight: number, // Height of the vehicle
    options: VehicleColorSetupOptions | null = null // Optional setup options.
  ) {
    this.options = options;
    this.imageWidth = imageWidth;
    this.imageHeight = imageHeight;

    // Retrieve the root element. Throws error if not found.
    this.root = document.querySelector(selector)!;
    if (!this.root) {
      console.error("Root element not found");
      return;
    }

    // Get all children elements of root.
    const divs = this.root.children;
    // Filter out elements that have both data-color and data-image attributes.
    const colorElements = this.getColorElements(divs);
    if (colorElements.length === 0) {
      console.error("No color elements found");
      return;
    }

    // Validate the provided options.
    if (!this.checkOptions()) {
      console.error("Invalid options");
      return;
    }

    // Clear the container and set up necessary wrapper elements.
    this.root.innerHTML = "";
    this.root.classList.add("vehicle-colors-container234890");

    // Create container divs for buttons and images.
    const buttonDiv = document.createElement("div");
    buttonDiv.classList.add("button-container523890");
    const imagesDiv = document.createElement("div");
    imagesDiv.classList.add("images-container423897");
    imagesDiv.style.maxWidth = `${this.imageWidth}px`;
    imagesDiv.style.maxHeight = `${this.imageHeight}px`;
    const subtitles = document.createElement("div");
    subtitles.classList.add("subtitles-container423897");

    // Loop through each color element and set up buttons and images.
    colorElements.forEach((element, i) => {
      // Retrieve the color(s) for the button and image from the element attribute.
      const colors = element.getAttribute("data-color")?.split(",")!;
      const image = element.getAttribute("data-image")!;
      // Get inner HTML and text for color name display.
      const colorNameHtml = element.innerHTML;
      const colorNameText = element.innerText;

      // Create a button element for color selection.
      const button = document.createElement("button");
      button.classList.add("color-button123789");
      // Create an image element for displaying the vehicle image.
      const img = document.createElement("img");
      img.classList.add("vehicle-image123789");
      const subtitle = document.createElement("div");
      subtitle.classList.add("subtitle123789");
      subtitle.innerHTML = colorNameHtml;
      img.src = image;
      img.alt = colorNameText;
      img.loading = "lazy";
      img.width = this.imageWidth;
      img.height = this.imageHeight;
      img.style.width = this.imageWidth + "px";
      img.style.maxWidth = "100%";
      img.style.maxHeight = "100%";

      // Create a span to hold the color name and append it to the button.
      const span = document.createElement("span");
      span.innerHTML = colorNameHtml;
      button.appendChild(span);
      // Set the background of the button using the computed gradient or color.
      button.style.background = this.getButtonColor(colors);

      // Mark the first button and image as active by default.
      if (i === 0) {
        button.classList.add("color-active");
        img.classList.add("color-active");
        subtitle.classList.add("color-active");
      }

      // Append buttons and images to their respective containers.
      buttonDiv.appendChild(button);
      imagesDiv.appendChild(img);
      subtitles.appendChild(subtitle);
      // Set up event listeners on the button for interactivity.
      this.setupListeners(button, img, subtitle);
    });

    // Append the images and buttons containers to the root element.
    this.root.appendChild(imagesDiv);
    this.root.appendChild(subtitles);
    this.root.appendChild(buttonDiv);
  }

  // Computes the button background color, which could be a gradient.
  private getButtonColor(colors: string[]): string {
    // If no color is provided, fallback to black.
    if (colors.length === 0) {
      return "#000000";
    }

    // Determine gradient smoothness and angle from options, or apply defaults.
    const gradientSmoothness =
      this.options?.gradientSmoothness === undefined
        ? 0.2
        : this.options.gradientSmoothness / 200;
    const angle =
      this.options?.gradientAngle === undefined
        ? 90
        : this.options.gradientAngle;

    // If only one color is provided, use it as the background.
    if (colors.length === 1) {
      return colors[0];
    }

    const numColors = colors.length;
    // Calculate the percentage step between color stops.
    const percentageStep = 100 / numColors;
    const stops: string[] = [];
    // Offset to adjust position of each stop for smooth gradients.
    const offset = percentageStep * gradientSmoothness;

    // Create gradient stops for each color.
    for (let i = 0; i < numColors; i++) {
      const start = i * percentageStep;
      const end = (i + 1) * percentageStep;

      // Ensure the first and last colors stick to the edges.
      const startStop = i === 0 ? 0 : start + offset;
      const endStop = i === numColors - 1 ? 100 : end - offset;

      stops.push(`${colors[i]} ${startStop}%`, `${colors[i]} ${endStop}%`);
    }

    // Return the constructed linear-gradient CSS string.
    return `linear-gradient(${angle}deg, ${stops.join(", ")})`;
  }

  // Validates that the provided options are within their defined ranges.
  private checkOptions(): boolean {
    if (
      this.options?.gradientSmoothness !== undefined &&
      (this.options.gradientSmoothness < 0 ||
        this.options.gradientSmoothness > 100)
    ) {
      console.error("gradientSmoothness should be between 0 and 100");
      return false;
    }
    if (
      this.options?.gradientAngle !== undefined &&
      (this.options.gradientAngle < 0 || this.options.gradientAngle > 360)
    ) {
      console.error("gradientAngle should be between 0 and 360");
      return false;
    }
    return true;
  }

  // Filters the given HTMLCollection for DIV elements that have both data-color and data-image attributes.
  private getColorElements(divs: HTMLCollection): HTMLElement[] {
    const elements: HTMLElement[] = [];
    for (let i = 0; i < divs.length; i++) {
      if (
        divs[i].nodeName === "DIV" &&
        divs[i].getAttribute("data-color") &&
        divs[i].getAttribute("data-image")
      ) {
        elements.push(divs[i] as HTMLElement);
      }
    }
    return elements;
  }

  // Adds a click listener to the button that toggles the active state of corresponding elements.
  private setupListeners(
    button: HTMLButtonElement,
    img: HTMLImageElement,
    subtitle: HTMLElement
  ) {
    button.addEventListener("click", () => {
      // Remove the active class from all active elements within the container.
      const activeElems = this.root.querySelectorAll(".color-active");
      activeElems.forEach((elem) => {
        elem.classList.remove("color-active");
      });

      // Activate the clicked button and its corresponding image.
      button.classList.add("color-active");
      img.classList.add("color-active");
      subtitle.classList.add("color-active");
    });
  }
}
